import React, { useEffect, useMemo, useRef, useState } from "react";
import { matchPath, useLocation } from "react-router";

import { ClickAwayListener } from "@mui/base/ClickAwayListener";
import CloseIcon from "@mui/icons-material/Close";
import CompareIcon from "@mui/icons-material/Compare";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import MenuIcon from "@mui/icons-material/Menu";
import NewspaperIcon from "@mui/icons-material/Newspaper";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import SsidChartIcon from "@mui/icons-material/SsidChart";
import { Box, CircularProgress, Divider, IconButton, Slide, Stack, Typography } from "@mui/material";
import { ReactComponent as Logo } from "assets/icons/logo.svg";
import { PortfolioOverviewIcon, PsychologyReverse, ReportGeneratorIcon } from "assets/material-custom-icons";
import { WorkspaceDocumentIcon } from "assets/material-custom-icons";
import { RoleComponentGuard } from "components";
import { AccountInvite } from "components/Account-sign-up";
import { FreemiumMessages, RoleName } from "constants/index";
import { Routes } from "constants/routes";
import { useDevice, useModals, useNavigate } from "hooks";
import { useActionHook } from "hooks/shared-hooks/use-action-hook";
import {
  useAccountSelector,
  useAuthActions,
  useBenchmarkSelector,
  useInvestorsSelector,
  usePortfolioConstructSelector,
  useReportsSelector,
  useUserPermissions,
  useUserRoleSelector,
  useUserSelector
} from "store";
import { ExtractRouteParams, PermissionValueType, RoutePath, RoutesKeys, UserPermissionType } from "store/types";
import { isRoleAnalyst } from "utils/user";

import { ReactComponent as AssetBenchmark } from "../../assets/icons/asset-benchmark.svg";
import { ReactComponent as BulletedList } from "../../assets/icons/bulleted-list-outline.svg";
import { ReactComponent as Cloud } from "../../assets/icons/cloud.svg";
import { ReactComponent as MyPortfolio } from "../../assets/icons/my-portfolio.svg";
import { ReactComponent as PortfolioIcon } from "../../assets/icons/portfolio-overview.svg";
import { ReactComponent as LogoTitle } from "../../assets/icons/sidebar-altius-title.svg";
import { ReactComponent as TaskIcon } from "../../assets/icons/task.svg";
import { AdminToolsMenu } from "../Sidebar/Sidebar-components/Admin-tools-menu";

interface menuItems {
  availableRoles: RoleName[];
  route: RoutesKeys;
  title: string;
  enabled?: boolean;
  icon?: React.ReactNode;
  option?: string;
  permission?: PermissionValueType;
  params?: ExtractRouteParams<RoutePath<RoutesKeys>>;
}

export const HamburgerMenu: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isMobile } = useDevice();
  const { infoModal } = useModals();
  const [open, setOpen] = useState(false);
  const [adminToolsOpen, setAdminToolsOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState("");

  const isBenchmarkEnabled = useBenchmarkSelector();
  const {
    allow_dataroom: dataRooms,
    show_plaid: isPlaidEnabled,
    allow_social: isSocialEnabled,
    allow_news: isDashboardEnabled,
    allow_account_invitation,
    external_report,
    enable_user_management,
    is_freemium,
    upgraded_from_freemium,
    show_smartsheet,
    show_ai_report_generator,
    show_ai_intelligence,
    enable_portfolio_construction
  } = useAccountSelector() || {};
  const cloudIntegrations = dataRooms || isPlaidEnabled;
  const isReportEnabled = useReportsSelector();
  const user = useUserSelector();
  const has_shared_deals = user.has_shared_deals;

  const userPermissions = useUserPermissions();
  const investors = useInvestorsSelector();
  const savedInvestorId = usePortfolioConstructSelector();
  const investorId =
    savedInvestorId && investors.some((i) => i.id === savedInvestorId) ? savedInvestorId : investors[0]?.id;
  const role = useUserRoleSelector();
  const { getReportGeneratorHashAction } = useAuthActions();

  const { action: getReportGeneratorHash, loading: reportGeneratorLoading } = useActionHook(
    getReportGeneratorHashAction,
    {
      onSuccess: (uuid) => {
        window.open(`https://intelligence.altius.finance/?sid=${uuid}`, "_blank");
      }
    }
  );

  const onCloseMenu = () => {
    setOpen(false);
    setAdminToolsOpen(false);
  };

  const handleNavigation = (route: RoutesKeys, params?: ExtractRouteParams<RoutePath<RoutesKeys>>) => {
    infoModal({
      title: FreemiumMessages.BEST_PRACTICE_RECOMMENDATION_TITLE,
      msg: FreemiumMessages.BEST_PRACTICE_RECOMMENDATION_MESSAGE,
      buttonText: "Continue"
    });
    navigate({ route, ...(params && { params }) });
    onCloseMenu();
  };

  const logoClick = () => {
    if (isRoleAnalyst({ role }) && is_freemium) {
      navigate({ route: "freemium" });
      onCloseMenu();
      return;
    }
    if (isRoleAnalyst({ role }) && upgraded_from_freemium) {
      navigate({ route: "freemium" });
      onCloseMenu();
      return;
    }
    if (isRoleAnalyst({ role }) && isDashboardEnabled) {
      handleNavigation("dashboard");
      return;
    }
    if (
      isRoleAnalyst({ role }) &&
      userPermissions?.["deals"] &&
      userPermissions?.["deals"] === UserPermissionType.NO_ACCESS
    ) {
      handleNavigation("reports");
      return;
    }
    handleNavigation("myDeals");
  };

  const dataroomRoute = useMemo(() => {
    if (
      role?.name === RoleName.TeamViewer ||
      (role?.name === RoleName.Analyst &&
        userPermissions?.["cloud_integrations"] &&
        userPermissions["cloud_integrations"] === UserPermissionType.VIEWER &&
        !is_freemium) ||
      (role?.name === RoleName.Analyst &&
        userPermissions?.["cloud_integrations"] &&
        userPermissions["cloud_integrations"] === UserPermissionType.EDITOR &&
        !is_freemium)
    ) {
      return "dataRoomsAllFiles";
    }

    return "dataRooms";
  }, [userPermissions]);

  const iconHoverStyles = { backgroundColor: "#0f3a54", borderRadius: "8px" };

  const menuItemStyles = {
    borderRadius: "8px",
    width: "100%",
    ":hover": { background: "#0f3a54", color: "#ffffff" },
    display: "flex",
    alignItems: "center",
    padding: "3px 7px 3px 7px",
    color: "#92a7bc",
    fontSize: "15px",
    cursor: "pointer"
  };

  useEffect(() => {
    const routeOptions: Record<string, string> = {
      [Routes.myDeals]: "my deals",
      [Routes.deal]: "my deals",
      [Routes.mySecurities]: "my securities",
      [Routes.benchmark]: "benchmark",
      [Routes.quickView]: "smart sheet",
      [Routes.taskManagement]: "task management",
      [Routes.dataRooms]: "data rooms",
      [Routes.monitoring]: "data operations",
      [Routes.monitoringData]: "additional reports",
      [Routes.comparison]: "comparison",
      [Routes.externalReport]: "external reports",
      [Routes.investorsList]: "admin tools",
      [Routes.measures]: "admin tools",
      [Routes.characteristics]: "admin tools",
      [Routes.assets]: "admin tools",
      [Routes.plaid]: "admin tools",
      [Routes.snapshot]: "portfolio analysis",
      [Routes.timeline]: "portfolio analysis",
      [Routes.classic]: "portfolio analysis",
      [Routes.cashflow]: "portfolio analysis",
      [Routes.social]: "Profile Network",
      [Routes.reportGenerator]: "report generator",
      [Routes.dashboard]: "dashboard",
      [Routes.newsFeed]: "news feed",
      [Routes.documents]: "workspace",
      [Routes.usersManagement]: "usersManagement",
      [Routes.users]: "usersManagement",
      [Routes.teams]: "usersManagement",
      [Routes.portfolioConstructionInvestor]: "portfolio construction"
    };

    const selectedOption = Object.keys(routeOptions).find((route) => matchPath(pathname, route));

    if (selectedOption) {
      setSelectedOption(routeOptions[selectedOption]);
    } else {
      setSelectedOption("");
    }
  }, [pathname]);

  const mainList: menuItems[] = [
    {
      availableRoles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer, RoleName.Guest, RoleName.Viewer],
      route: "newsFeed",
      enabled: true,
      title: "News Feed",
      icon: <NewspaperIcon sx={{ fill: "#92a7bc", width: "17px", height: "17px" }} />,
      option: "news feed"
    },
    {
      availableRoles: [RoleName.Admin, RoleName.Analyst],
      route: "dashboard",
      enabled: isDashboardEnabled,
      title: "Dashboard",
      icon: <DashboardOutlinedIcon sx={{ fill: "#92a7bc", width: "17px", height: "17px" }} />,
      option: "dashboard"
    },
    {
      availableRoles: [
        RoleName.Admin,
        RoleName.Analyst,
        RoleName.TeamViewer,
        RoleName.Guest,
        RoleName.Viewer,
        RoleName.Investor
      ],
      route: "myDeals",
      enabled: has_shared_deals === null ? true : has_shared_deals,
      title: "My Deals",
      icon: <MyPortfolio style={{ fill: "#92a7bc", width: "14px" }} />,
      option: "my deals",
      ...(userPermissions?.["deals"] && { permission: userPermissions["deals"] })
    },
    {
      availableRoles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer, RoleName.Investor],
      route: "reports",
      enabled: isReportEnabled,
      title: "Portfolio Analysis",
      icon: <PortfolioIcon style={{ fill: "#92a7bc", width: "14px", height: "17px" }} />,
      option: "portfolio analysis",
      ...(userPermissions?.["portfolio_analysis"] && { permission: userPermissions["portfolio_analysis"] })
    },
    {
      availableRoles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer],
      route:
        userPermissions?.["deal_metrics"] && userPermissions["deal_metrics"] === UserPermissionType.NO_ACCESS
          ? "fileRepository"
          : "monitoringData",
      enabled: isReportEnabled,
      title: "Data Operations",
      icon: <EqualizerIcon sx={{ fill: "#92a7bc", width: "17px", height: "17px" }} />,
      option: "data operations"
    },
    {
      availableRoles: [RoleName.Admin, RoleName.Analyst],
      route: "reportGenerator",
      enabled: show_ai_report_generator,
      title: "Report Generator",
      icon: <ReportGeneratorIcon sx={{ fill: "#92a7bc", width: "14px", height: "15px" }} />,
      option: "report generator"
    }
  ];

  const subList: menuItems[] = [
    {
      availableRoles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer],
      route: "taskManagement",
      enabled: true,
      title: "Task Management",
      icon: <TaskIcon style={{ fill: "#92a7bc", width: "14px", height: "17px" }} />,
      option: "task management",
      ...(userPermissions?.["task_manager"] && { permission: userPermissions["task_manager"] })
    },
    {
      availableRoles: [RoleName.Admin, RoleName.Analyst],
      route: "quickView",
      enabled: show_smartsheet,
      title: "Smart Sheet",
      icon: <BulletedList style={{ fill: "#92a7bc", width: "17px", height: "17px" }} />,
      option: "smart sheet",
      ...(userPermissions?.["smart_sheet"] && { permission: userPermissions["smart_sheet"] })
    },
    {
      availableRoles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer],
      route: dataroomRoute,
      enabled: cloudIntegrations,
      title: "Cloud Integrations",
      icon: <Cloud style={{ fill: "#92a7bc", width: "14px", height: "15px" }} />,
      option: "data rooms",
      ...(userPermissions?.["cloud_integrations"] && { permission: userPermissions["cloud_integrations"] })
    },
    {
      availableRoles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer],
      route: "benchmark",
      enabled: isBenchmarkEnabled,
      title: "Benchmark",
      icon: <AssetBenchmark style={{ fill: "#92a7bc", width: "14px", height: "17px" }} />,
      option: "benchmark",
      ...(userPermissions?.["benchmark"] && { permission: userPermissions["benchmark"] })
    },
    {
      availableRoles: [
        RoleName.Admin,
        RoleName.Analyst,
        RoleName.TeamViewer,
        RoleName.Guest,
        RoleName.Viewer,
        RoleName.Investor
      ],
      route: "workspaceFolders",
      enabled: true,
      title: "Workspace",
      icon: <WorkspaceDocumentIcon style={{ fill: "#92a7bc", width: "17px", height: "19px" }} />,
      option: "workspace"
    },
    {
      availableRoles: [RoleName.Admin, RoleName.Analyst],
      route: "portfolioConstructionInvestor",
      enabled: Boolean(investorId) && enable_portfolio_construction,
      params: { investor_id: investorId, tab: "allocation" },
      title: "Portfolio Construction",
      icon: <PortfolioOverviewIcon sx={{ fill: "#92a7bc", width: "14px", height: "17px" }} />,
      option: "portfolio construction",
      ...(userPermissions?.["portfolio_construction"] && { permission: userPermissions["portfolio_construction"] })
    },
    {
      availableRoles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer],
      route: "social",
      enabled: isSocialEnabled,
      title: "Profile Network",
      icon: <PeopleAltOutlinedIcon sx={{ color: "#92a7bc", width: "14px", height: "15px" }} />,
      option: "Profile Network",
      ...(userPermissions?.["social"] && { permission: userPermissions["social"] })
    },
    {
      availableRoles: [RoleName.Admin, RoleName.Analyst],
      route: "externalReport",
      enabled: external_report,
      title: "External Reports",
      icon: <SsidChartIcon sx={{ color: "#92a7bc", width: "14px", height: "15px" }} />,
      option: "external reports",
      ...(userPermissions?.["external_reports"] && { permission: userPermissions["external_reports"] })
    },
    {
      availableRoles: [RoleName.Admin],
      route: "usersManagement",
      enabled: enable_user_management,
      title: "User Management",
      icon: <PeopleAltIcon style={{ fill: "#92a7bc", width: "14px", height: "17px" }} />,
      option: "usersManagement"
    },
    {
      availableRoles: [RoleName.Admin, RoleName.Analyst, RoleName.TeamViewer],
      route: "comparison",
      enabled: import.meta.env.REACT_APP_NODE_ENV !== "production",
      title: "Comparison",
      icon: <CompareIcon sx={{ color: "#92a7bc", width: "14px", height: "15px" }} />,
      option: "comparison"
    }
  ];

  const menuRef = useRef<HTMLDivElement>(null);

  if (!isMobile) {
    return null;
  }

  return (
    <Box sx={{ position: "relative" }} ref={menuRef}>
      <ClickAwayListener onClickAway={onCloseMenu}>
        <Box>
          <IconButton
            onClick={() => setOpen(!open)}
            sx={{
              color: "#92a7bc",
              padding: "8px",
              borderRadius: "32px",
              position: "relative",
              width: "40px",
              height: "40px"
            }}
          >
            <Box
              sx={{
                position: "absolute",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                height: "100%",
                transition: "opacity 0.3s ease, transform 0.3s ease"
              }}
            >
              <MenuIcon
                sx={{
                  fontSize: "24px",
                  opacity: open ? 0 : 1,
                  transform: open ? "rotate(90deg)" : "rotate(0deg)",
                  position: "absolute",
                  transition: "opacity 0.3s ease, transform 0.3s ease"
                }}
              />
              <CloseIcon
                sx={{
                  fontSize: "24px",
                  opacity: open ? 1 : 0,
                  transform: open ? "rotate(0deg)" : "rotate(-90deg)",
                  position: "absolute",
                  transition: "opacity 0.3s ease, transform 0.3s ease"
                }}
              />
            </Box>
          </IconButton>
          <Slide direction="down" in={open} timeout={300} mountOnEnter unmountOnExit>
            <Box
              sx={{
                position: "fixed",
                top: "40px", // Height of mobile header
                left: 0,
                right: 0,
                width: "100%",
                height: "calc(100vh - 40px)",
                backgroundColor: "#071f35",
                boxShadow: "0px 10px 15px -3px rgba(18, 18, 23, 0.08), 0px 4px 6px -2px rgba(18, 18, 23, 0.05)",
                zIndex: 1300,
                overflowY: "auto",
                borderBottom: "0.5px solid #dae0e2",
                display: "flex",
                flexDirection: "column"
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-start",
                  minHeight: 0
                }}
              >
                <Box
                  sx={{
                    padding: "20px 15px 20px 15px",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "stretch",
                    justifyContent: "flex-start",
                    gap: "24px",
                    overflowY: "auto",
                    flexGrow: 1
                  }}
                >
                  <Box
                    sx={{
                      whiteSpace: "nowrap",
                      width: "100%",
                      overflow: "hidden",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center"
                    }}
                  >
                    <Box
                      sx={{ cursor: "pointer", display: "inline-flex", alignItems: "center" }}
                      onClick={() => logoClick()}
                    >
                      <Logo style={{ width: "25px", height: "26px", marginRight: "7px" }} />
                      <LogoTitle style={{ height: "26px", width: "80px" }} />
                    </Box>
                  </Box>
                  <Stack sx={{ gap: "8px" }}>
                    {mainList.map(
                      ({ availableRoles, route, enabled, title, icon, option, permission }, index) =>
                        enabled && (
                          <RoleComponentGuard
                            availableRoles={availableRoles}
                            key={index}
                            hideChild={permission && permission === UserPermissionType.NO_ACCESS}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap="10px"
                              justifyContent="flex-start"
                              onClick={() => {
                                handleNavigation(route);
                              }}
                              sx={{
                                ...(selectedOption === option && {
                                  ...iconHoverStyles,
                                  color: "#ffffff",
                                  "& svg": { fill: "#ffffff !important", color: "#ffffff !important" }
                                }),
                                ...menuItemStyles
                              }}
                            >
                              {icon}
                              <Typography sx={{ whiteSpace: "nowrap", overflow: "hidden", fontSize: "20px" }}>
                                {title}
                              </Typography>
                            </Stack>
                          </RoleComponentGuard>
                        )
                    )}
                  </Stack>

                  <Divider sx={{ borderColor: "#496682" }} />

                  <Stack sx={{ gap: "8px" }}>
                    {subList.map(
                      ({ availableRoles, route, params, enabled, title, icon, option, permission }, index) =>
                        enabled && (
                          <RoleComponentGuard
                            availableRoles={availableRoles}
                            key={index}
                            hideChild={permission && permission === UserPermissionType.NO_ACCESS}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap="10px"
                              justifyContent="flex-start"
                              onClick={() => {
                                handleNavigation(route, params);
                              }}
                              sx={{
                                ...(selectedOption === option && {
                                  ...iconHoverStyles,
                                  color: "#ffffff",
                                  "& svg": { fill: "#ffffff !important", color: "#ffffff !important" }
                                }),
                                ...menuItemStyles
                              }}
                            >
                              {icon}
                              <Typography sx={{ whiteSpace: "nowrap", overflow: "hidden", fontSize: "20px" }}>
                                {title}
                              </Typography>
                            </Stack>
                          </RoleComponentGuard>
                        )
                    )}
                  </Stack>
                  {Boolean(show_ai_intelligence) && (
                    <RoleComponentGuard availableRoles={[RoleName.Admin, RoleName.Analyst]}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        gap="10px"
                        onClick={() => {
                          getReportGeneratorHash();
                          onCloseMenu();
                        }}
                        sx={{
                          ...menuItemStyles,
                          ...(reportGeneratorLoading && { pointerEvents: "none", opacity: 0.7 })
                        }}
                      >
                        {reportGeneratorLoading ? (
                          <CircularProgress size={14} />
                        ) : (
                          <PsychologyReverse sx={{ fill: "#92a7bc", width: "14px", height: "15px" }} />
                        )}
                        <Typography sx={{ whiteSpace: "nowrap", overflow: "hidden", fontSize: "20px" }}>
                          Data Intelligence
                        </Typography>
                      </Stack>
                    </RoleComponentGuard>
                  )}
                </Box>
                <Box sx={{ position: "relative", flexShrink: 0 }}>
                  <RoleComponentGuard availableRoles={[RoleName.Admin]}>
                    <Stack gap="20px" width="100%" alignItems="center" sx={{ padding: "10px 15px" }}>
                      {allow_account_invitation && <AccountInvite />}
                    </Stack>
                  </RoleComponentGuard>
                  <AdminToolsMenu
                    setAdminToolsOpen={setAdminToolsOpen}
                    adminToolsOpen={adminToolsOpen}
                    selectedOption={selectedOption}
                    onCloseSidebar={onCloseMenu}
                    onNavigate={(route) => handleNavigation(route as RoutesKeys)}
                    isHamburgerMenu
                  />
                </Box>
              </Box>
            </Box>
          </Slide>
        </Box>
      </ClickAwayListener>
    </Box>
  );
};
